import { useEffect, useRef, useState } from 'react'
import { Bell, BellOff } from 'lucide-react'

interface NotificationBellProps {
  /** Unread count; the badge is hidden at 0. */
  count?: number
}

/** Header bell owning its own notifications dropdown. */
export default function NotificationBell({ count = 0 }: NotificationBellProps) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return

    function handleMouseDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('mousedown', handleMouseDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleMouseDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-label="Notifications"
        aria-haspopup="menu"
        aria-expanded={open}
        className="p-1.5 rounded hover:bg-gray-100 transition-colors text-gray-400 hover:text-primary"
      >
        <Bell size={18} />
      </button>
      {count > 0 && (
        <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full bg-primary text-white text-[10px] flex items-center justify-center px-1">
          {count > 9 ? '9+' : count}
        </span>
      )}

      {open && (
        <div className="absolute right-0 top-full mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-xs font-semibold text-gray-800">Notifications</p>
          </div>
          <div className="flex flex-col items-center gap-1 px-4 py-8 text-center">
            <BellOff size={22} className="text-gray-300" />
            <p className="text-xs font-medium text-gray-700">You&apos;re all caught up</p>
            <p className="text-[11px] text-gray-400">Notifications will appear here.</p>
          </div>
        </div>
      )}
    </div>
  )
}
